import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Label from 'LuxKit/Label'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import TextLink from 'LuxKit/TextLink'
import IconButton from 'LuxKit/Button/IconButton'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import zIndex from 'styles/tools/z-index'

const BannerWrapper = styled.div`
  z-index: ${zIndex.min};
  display: flex;
  justify-content: center;
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  color: ${props => props.theme.palette.highlight.primary.lightContrast};
  padding: ${rem(12)} ${rem(8)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(12)} ${rem(16)};
  }
`

const InnerRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: ${rem(800)};
`

const ArrowCell = styled.div<{ $hidden: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${rem(4)};
  visibility: ${props => props.$hidden ? 'hidden' : 'visible'};

  ${mediaQueryUp.tablet} {
    padding: 0 ${rem(8)};
  }
`

const SlidesViewport = styled.div`
  flex: 1;
  overflow: hidden;
  min-width: 0;
`

const SlidesTrack = styled.div<{ $activeIndex: number }>`
  --lux-plus-promo-banner-carousel-direction: 1;

  display: flex;
  transition: transform 0.3s ease-in-out;
  transform: translateX(calc(${props => props.$activeIndex * -100}% * var(--lux-plus-promo-banner-carousel-direction)));

  [dir='rtl'] & {
    --lux-plus-promo-banner-carousel-direction: -1;
  }
`

const Slide = styled.div`
  flex: 0 0 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`

export type PromoBannerCtaLinkType = 'sign-up' | 'link'

export interface PromoBannerItem {
  label: string
  title: string
  ctaText: string
  ctaLink: string
  ctaLinkType?: PromoBannerCtaLinkType
  expiresAt?: string
  eligibleRegions: Array<string>
}

interface Props {
  items: Array<PromoBannerItem>
  /** Whether banner carousel arrow icons should mirror in RTL. */
  arrowIconRtlFlip?: boolean
}

function LuxPlusPromoBannerCarousel({ items, arrowIconRtlFlip = false }: Props) {
  const [activeIndex, setActiveIndex] = useState(0)
  const { handleBecomeMember } = useLuxPlusLandingActions()

  const handlePrev = useCallback(() => {
    setActiveIndex(prev => Math.max(prev - 1, 0))
  }, [])

  const handleNext = useCallback(() => {
    setActiveIndex(prev => Math.min(prev + 1, items.length - 1))
  }, [items.length])

  if (items.length === 0) return null

  const showArrows = items.length > 1
  const isAtStart = activeIndex === 0
  const isAtEnd = activeIndex === items.length - 1

  return (
    <BannerWrapper>
      <InnerRow>
        {showArrows && (
          <ArrowCell $hidden={isAtStart}>
            <IconButton onClick={handlePrev} kind="tertiary" shape="circle" size="small">
              <LineAngleLeftIcon rtlFlip={arrowIconRtlFlip} />
            </IconButton>
          </ArrowCell>
        )}
        <SlidesViewport>
          <SlidesTrack $activeIndex={activeIndex}>
            {items.map((item, index) => (
              <Slide key={index}>
                <Group direction="vertical" tabletDirection="horizontal" gap={8} horizontalAlign="center" verticalAlign="center">
                  <Label variant="highlight" kind="primary">
                    {item.label}
                  </Label>
                  <BodyText variant="medium">{item.title}</BodyText>
                  <TextLink
                    size="medium"
                    weight="bold"
                    underline
                    {...(item.ctaLinkType === 'sign-up'
                      ? { onClick: handleBecomeMember }
                      : { to: item.ctaLink })}
                  >
                    {item.ctaText}
                  </TextLink>
                </Group>
              </Slide>
            ))}
          </SlidesTrack>
        </SlidesViewport>
        {showArrows && (
          <ArrowCell $hidden={isAtEnd}>
            <IconButton onClick={handleNext} kind="tertiary" shape="circle" size="small">
              <LineAngleRightIcon rtlFlip={arrowIconRtlFlip} />
            </IconButton>
          </ArrowCell>
        )}
      </InnerRow>
    </BannerWrapper>
  )
}

export default LuxPlusPromoBannerCarousel
